/**
 * <collection-highlights> — auto-rotating "Collection Highlights" carousel used in the
 * header mega menu (desktop) and the mobile drawer.
 *
 * - Cross-fades between slides on a timer (data-autoplay ms, default 3000).
 * - Prev/next arrows (desktop) + dots; manual nav restarts the timer.
 * - Pauses only when off-screen / menu closed (IntersectionObserver). It does NOT
 *   pause on hover: the mega menu is opened *by* hovering, so a hover-pause would
 *   stop the carousel exactly when it's visible.
 * - Respects prefers-reduced-motion (no autoplay; arrows/dots still work).
 *
 * Loaded once on page load from header-drawer.liquid so the element is defined and
 * auto-upgrades the instances injected lazily into the menu via section hydration.
 * Markup is rendered by snippets/collection-highlights.liquid.
 */
class CollectionHighlights extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-ch-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-ch-dot]'));
    this.timer = null;
    this.interval = parseInt(this.dataset.autoplay || '3000', 10);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.slides.length <= 1) return;

    const current = this.slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
    this.index = current < 0 ? 0 : current;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.go(parseInt(dot.dataset.chDot, 10));
        this.restart();
      });
    });

    this.querySelectorAll('[data-ch-prev]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.go(this.index - 1);
        this.restart();
      });
    });

    this.querySelectorAll('[data-ch-next]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.go(this.index + 1);
        this.restart();
      });
    });

    // Pause when off-screen (drawer closed, or submenu content hidden); resume when shown.
    this.io = new IntersectionObserver(
      (entries) => entries.forEach((entry) => (entry.isIntersecting ? this.start() : this.stop())),
      { threshold: 0 }
    );
    this.io.observe(this);

    this.start();
  }

  disconnectedCallback() {
    this.stop();
    if (this.io) this.io.disconnect();
  }

  go(i) {
    const count = this.slides.length;
    this.index = ((i % count) + count) % count;
    this.slides.forEach((slide, n) => {
      slide.setAttribute('aria-hidden', n === this.index ? 'false' : 'true');
    });
    this.dots.forEach((dot, n) => {
      if (n === this.index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  next() {
    this.go(this.index + 1);
  }

  start() {
    if (this.reduceMotion || this.timer || this.slides.length <= 1) return;
    this.timer = setInterval(() => this.next(), this.interval);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    this.stop();
    this.start();
  }
}

if (!customElements.get('collection-highlights')) {
  customElements.define('collection-highlights', CollectionHighlights);
}
